import React, { FC, useState, useRef, ReactNode } from 'react';
import translate from '@translate';
import { checkFormValidity } from 'src/utils/formFunctions';
import Grid, { GridItem } from 'src/components/Grid';
import Button from 'src/components/Button';
import Typography from 'src/components/Typography';
import { sizes } from 'src/themes/default';
import { EditKeysType } from './ItemsTable';
import { ConstantsCodeType } from 'src/types/constantsCodes';
import { TableColumnType } from 'src/types/table';
import Select from 'src/components/Select';
import { langCodes } from 'src/resources/constantsCodes/constants';
import TextField from 'src/components/TextField';
import { EditConstantsCodeType } from './index';
import { FormErrorsType } from '../../types/global';

type NewItemBlockPropsType = {
    data?: EditConstantsCodeType[];
    onChange: (value: any) => void;
    disableEdit?: boolean;
    constantKey: string;
    allColumns: TableColumnType[];
    allConstantsCodes: {
        [key: string]: ConstantsCodeType[];
    };
    countryCodes: ConstantsCodeType[];
};

const initData: EditConstantsCodeType = {};

const NewItemBlock: FC<NewItemBlockPropsType> = (props) => {
    const formRef = useRef<HTMLFormElement>(null);

    const [editData, setEditData] = useState<EditConstantsCodeType>(initData);
    const [editErrors, setEditErrors] = useState<FormErrorsType>({});

    const changeValue = (key: EditKeysType, value: any, error: boolean) => {
        const newData = { ...editData };
        const newErrors = { ...editErrors };
        newData[key] = value;
        newErrors[key] = error;
        setEditData(newData);
        setEditErrors(newErrors);
    };

    const handleAddRow = () => {
        const newFormErrors = checkFormValidity(formRef);

        if (!Object.keys(newFormErrors).length) {
            const newId = Math.min.apply(0, [0, ...(props.data || []).map((item: any): any => item.ID)]) - 1;

            props.onChange([
                ...(props.data || []),
                {
                    ...editData,
                    ID: newId,
                },
            ]);

            setEditData(initData);
        }

        setEditErrors(newFormErrors);
    };

    const getItemBlock = (column: TableColumnType): ReactNode => {
        switch (column.key) {
            case 'rawLabel':
                return (
                    <GridItem cols={props.constantKey === 'shipperMessageCodes' ? 6 : 3} key={column.key}>
                        <TextField
                            value={editData.rawLabel}
                            error={editErrors.rawLabel}
                            label={translate('constantDetail.table.label')}
                            onChange={(value, error) => changeValue('rawLabel', value, error)}
                            name="rawLabel"
                            fullWidth
                            required
                            disabled={props.disableEdit}
                            multiline={props.constantKey === 'shipperMessageCodes'}
                        />
                    </GridItem>
                );
            case 'value':
                return (
                    <GridItem cols={props.constantKey === 'shipperMessageCodes' ? 6 : 3} key={column.key}>
                        <TextField
                            value={editData.value}
                            error={editErrors.value}
                            label={translate('constantDetail.table.value')}
                            onChange={(value, error) => changeValue('value', value, error)}
                            name="value"
                            fullWidth
                            required
                            disabled={props.disableEdit}
                            multiline={props.constantKey === 'shipperMessageCodes'}
                        />
                    </GridItem>
                );
            case 'color':
                return <div style={{ backgroundColor: editData.color }}>{editData.color}</div>;
            case 'shipperCode':
                return (
                    <GridItem cols={2} key={column.key}>
                        <Select
                            options={[...props.allConstantsCodes.shipperCodes].sort((a, b) =>
                                a.label.localeCompare(b.label)
                            )}
                            value={editData.shipperCode}
                            error={editErrors.shipperCode}
                            label={translate('constantDetail.table.shipperCode')}
                            onChange={(value, error) => changeValue('shipperCode', value, error)}
                            name="shipperCode"
                            fullWidth
                            required
                            disabled={props.disableEdit}
                        />
                    </GridItem>
                );
            case 'isReturn':
                return (
                    <GridItem cols={1} key={column.key}>
                        <Select
                            options={props.allConstantsCodes.boolNumberCodes}
                            value={editData.isReturn}
                            error={editErrors.isReturn}
                            label={translate('constantDetail.table.isReturn')}
                            onChange={(value, error) => changeValue('isReturn', value, error)}
                            name="isReturn"
                            fullWidth
                            required
                            disabled={props.disableEdit}
                        />
                    </GridItem>
                );
            case 'isExpedition':
                return (
                    <GridItem cols={1} key={column.key}>
                        <Select
                            options={props.allConstantsCodes.boolNumberCodes}
                            value={editData.isExpedition}
                            error={editErrors.isExpedition}
                            label={translate('constantDetail.table.isExpedition')}
                            onChange={(value, error) => changeValue('isExpedition', value, error)}
                            name="isExpedition"
                            fullWidth
                            required
                            disabled={props.disableEdit}
                        />
                    </GridItem>
                );
            case 'LANG':
                return (
                    <GridItem cols={2} key={column.key}>
                        <Select
                            options={langCodes}
                            value={editData.LANG}
                            error={editErrors.LANG}
                            label={translate('constantDetail.table.lang')}
                            onChange={(value, error) => changeValue('LANG', value, error)}
                            name=""
                            fullWidth
                            required
                            disabled={props.disableEdit}
                        />
                    </GridItem>
                );
            case 'countryCodesSelect':
                return (
                    <GridItem cols={2} key={column.key}>
                        <Select
                            options={props.countryCodes}
                            value={editData.COUNTRY_SERVICE_CODES}
                            error={editErrors.COUNTRY_SERVICE_CODES}
                            label={translate('constantDetail.table.countryCodesSelect')}
                            onChange={(value, error) => changeValue('COUNTRY_SERVICE_CODES', value, error)}
                            name=""
                            fullWidth
                            disabled={props.disableEdit}
                            multiple
                        />
                    </GridItem>
                );
        }
        return null;
    };

    return (
        <>
            <Typography gutterBottom variant="h3">
                {translate('constantDetail.action.addItem')}
            </Typography>
            <form ref={formRef}>
                <Grid>
                    {props.allColumns.map((column) => getItemBlock(column))}
                    <GridItem cols={2}>
                        <Button
                            onClick={handleAddRow}
                            disabled={props.disableEdit}
                            sx={{ marginTop: sizes.input_top_PD }}
                            mobileFullWidht
                        >
                            + {translate('constantDetail.itemsTable.action.add')}
                        </Button>
                    </GridItem>
                </Grid>
            </form>
        </>
    );
};

export default NewItemBlock;
